'use client';

import React, { useState, useEffect } from 'react';
import { IconFlag, IconShare, IconBulb } from '@repo/svgs';
import { FilterChip } from '../../ui/FilterChip';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import { setHelpfulReview } from '@repo/api';
import dynamic from 'next/dynamic';
import { ShareButton } from '@/components/share/ShareButton';
import { useAnalytics } from '@/hooks/useAnalytics';

// Lazy load the report modal
const ReportReviewModal = dynamic(() =>
  import('./ReportReviewModal').then(mod => mod.ReportReviewModal),
);

export interface ReviewActionsProps {
  /** Review ID */
  reviewId: string;
  /** Address slug (required by some legacy review events) */
  addressSlug?: string;
  /** Current helpful count */
  helpfulCount?: number;
  /** Whether the review is already marked as helpful by current user */
  isHelpful?: boolean;
  /** Event screen */
  eventScreen?: 'building' | 'contact' | 'other';
}

export function ReviewActions({
  reviewId,
  addressSlug,
  helpfulCount = 0,
  isHelpful = false,
  eventScreen = 'other',
}: ReviewActionsProps) {
  const analytics = useAnalytics();
  const [localHelpfulCount, setLocalHelpfulCount] = useState(helpfulCount);
  const [localIsHelpful, setLocalIsHelpful] = useState(isHelpful);
  const [isHelpfulLoading, setIsHelpfulLoading] = useState(false);
  const [isReported, setIsReported] = useState(false);

  const screen: 'building' | 'contact' | null =
    eventScreen === 'building' ? 'building' : eventScreen === 'contact' ? 'contact' : null;

  // Sync local state with props when they change
  useEffect(() => {
    setLocalHelpfulCount(helpfulCount);
    setLocalIsHelpful(isHelpful);
  }, [helpfulCount, isHelpful]);

  const handleHelpful = async () => {
    if (isHelpfulLoading) return;

    // Optimistic update
    const newIsHelpful = !localIsHelpful;
    setLocalIsHelpful(newIsHelpful);
    setLocalHelpfulCount(prev => (newIsHelpful ? (prev || 0) + 1 : (prev || 0) - 1));

    if (screen && addressSlug) {
      analytics.track(newIsHelpful ? `${screen}_review_helpful` : `${screen}_review_unhelpful`, {
        reviewId,
        addressSlug,
      });
    }

    setIsHelpfulLoading(true);
    try {
      await setHelpfulReview(reviewId);
    } catch (error) {
      console.error('Error toggling helpful review:', error);
      // Revert on error
      setLocalIsHelpful(!newIsHelpful);
      setLocalHelpfulCount(prev => (!newIsHelpful ? (prev || 0) + 1 : (prev || 0) - 1));
      // Could show a toast notification here
    } finally {
      setIsHelpfulLoading(false);
    }
  };

  const handleReportSuccess = () => {
    setIsReported(true);
    if (screen) {
      analytics.track(`${screen}_review_report_submit`, { reviewId });
    }
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        {/* Helpful button */}
        <FilterChip
          showTrailingIcon={false}
          label={
            <>
              <span>Helpful</span>
              {localHelpfulCount > 0 && (
                <>
                  <span className="text-secondary"> · </span>
                  <span className="text-secondary">{localHelpfulCount}</span>
                </>
              )}
            </>
          }
          leadingIcon={<IconBulb className="w-4 h-4 icon-primary" />}
          active={localIsHelpful}
          onClick={handleHelpful}
          disabled={isHelpfulLoading}
          className="!px-3 !py-2 text-body-2"
        />

        {/* Report button */}
        <ModalTrigger
          label="Report a review"
          content={<ReportReviewModal reviewId={reviewId} onSuccess={handleReportSuccess} />}
          maxWidth="md"
          onOpen={() => {
            if (screen && addressSlug) {
              analytics.track(`${screen}_review_report`, { reviewId, addressSlug });
            }
          }}
        >
          <button
            disabled={isReported}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-body-2 font-semibold text-alert hover:opacity-80 transition-opacity disabled:opacity-50"
          >
            <IconFlag className="w-4 h-4" />
            <span>Report</span>
          </button>
        </ModalTrigger>
      </div>

      {/* Share button */}
      <ShareButton
        title="Review"
        className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-primary-hover active:bg-primary-press transition-colors"
        aria-label="Share review"
        icon={<IconShare className="w-4 h-4 icon-secondary" />}
        onShared={() => {
          if (screen) {
            analytics.track(`${screen}_review_share_openigloo`);
          }
        }}
      />
    </div>
  );
}
